import React from 'react';
import { Star, Quote } from 'lucide-react';
import { Heading, BodyText, SmallText } from './brand/BrandTypography';

// Placeholder testimonials - these can be replaced with real ones later
const DEFAULT_TESTIMONIALS = [
    {
        id: 1,
        text: "CoRegulate helped me understand my overwhelm for the first time. The gentle assessments and breathing tools are exactly what my ADHD brain needed.",
        author: "Sarah M.",
        role: "ADHD Adult",
        rating: 5,
        avatar: "S"
    },
    {
        id: 2,
        text: "As a partner of someone with ADHD, this app helped me understand what they're going through. The relationship tools are invaluable for our connection.",
        author: "James R.",
        role: "Partner",
        rating: 5,
        avatar: "J"
    },
    {
        id: 3,
        text: "I feel calmer within seconds using the micro-reset. It's like having a supportive friend in my pocket who actually gets it.",
        author: "Maya L.",
        role: "ADHD Adult",
        rating: 5,
        avatar: "M"
    },
    {
        id: 4,
        text: "The hypnotherapy elements combined with practical tools make this unlike any other mental health app I've tried. Professional yet accessible.",
        author: "David K.",
        role: "Therapist",
        rating: 5,
        avatar: "D"
    },
    {
        id: 5,
        text: "Finally, an app designed for how my brain actually works. No overwhelming walls of text, just gentle guidance when I need it most.",
        author: "Emma T.",
        role: "ADHD Adult",
        rating: 5,
        avatar: "E"
    },
    {
        id: 6,
        text: "The AI guide felt like talking to someone who truly understands ADHD. It recommended tools that actually helped me calm down during a panic moment.",
        author: "Chris P.",
        role: "ADHD Adult",
        rating: 5,
        avatar: "C"
    }
];

// Single Testimonial Card
const TestimonialCard = ({ testimonial, variant = 'default', index = 0 }) => {
    const { text, author, role, rating, avatar } = testimonial;
    
    const variantClasses = {
        default: 'glass-card',
        sage: 'glass-card-sage',
        lavender: 'glass-card-lavender',
        rose: 'glass-card-rose',
        blue: 'glass-card-blue',
        gold: 'glass-card-gold'
    };

    const variants = ['sage', 'lavender', 'rose', 'blue', 'gold', 'default'];
    const selectedVariant = variant === 'auto' ? variants[index % variants.length] : variant;
    
    return (
        <div 
            className={`${variantClasses[selectedVariant]} rounded-2xl p-6 md:p-8 brand-slide-up card-hover relative`}
            style={{ animationDelay: `${index * 100}ms` }}
            data-testid={`testimonial-card-${index}`}
        >
            {/* Quote icon */}
            <div className="absolute top-4 right-4 opacity-10">
                <Quote className="w-10 h-10 text-[#1D6B6B]" />
            </div>
            
            {/* Stars */}
            <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                    <Star 
                        key={i} 
                        className={`w-4 h-4 ${i < rating ? 'text-[#E4D3A3] fill-[#E4D3A3]' : 'text-[#E4D3A3]/30'}`}
                    />
                ))}
            </div>
            
            {/* Testimonial text */}
            <BodyText className="leading-relaxed text-[#2A8585] mb-6 italic">
                "{text}"
            </BodyText>
            
            {/* Author info */}
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center text-white font-medium text-sm">
                    {avatar}
                </div>
                <div>
                    <SmallText className="font-medium text-[#1D6B6B]">{author}</SmallText>
                    <SmallText className="text-[#7D8E95] text-xs">{role}</SmallText>
                </div>
            </div>
        </div>
    );
};

// Featured Testimonial (larger, more prominent)
const FeaturedTestimonial = ({ testimonial }) => {
    const { text, author, role, rating, avatar } = testimonial;
    
    return (
        <div 
            className="glass-card-sage rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
            data-testid="featured-testimonial"
        >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-[#1D6B6B]/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#FFBB98]/10 rounded-full translate-x-1/2 translate-y-1/2" />
            
            {/* Quote icon */}
            <div className="mb-6 flex justify-center">
                <div className="w-14 h-14 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center">
                    <Quote className="w-7 h-7 text-[#1D6B6B]" />
                </div>
            </div>
            
            {/* Stars */}
            <div className="flex gap-1 justify-center mb-6">
                {[...Array(5)].map((_, i) => (
                    <Star 
                        key={i} 
                        className={`w-5 h-5 ${i < rating ? 'text-[#E4D3A3] fill-[#E4D3A3]' : 'text-[#E4D3A3]/30'}`}
                    />
                ))}
            </div>
            
            {/* Testimonial text */}
            <BodyText className="text-lg md:text-xl leading-relaxed text-[#2A8585] mb-8 italic max-w-2xl mx-auto">
                "{text}"
            </BodyText>
            
            {/* Author info */}
            <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center text-white font-semibold">
                    {avatar}
                </div>
                <div className="text-left">
                    <SmallText className="font-semibold text-[#1D6B6B]">{author}</SmallText>
                    <SmallText className="text-[#7D8E95]">{role}</SmallText>
                </div>
            </div>
        </div>
    );
};

// Main Testimonials Section Component
const TestimonialsSection = ({ 
    testimonials = DEFAULT_TESTIMONIALS,
    title = "What people say",
    subtitle = "Real experiences from our community",
    showFeatured = true,
    maxItems = 6,
    layout = 'grid' // 'grid' | 'carousel' | 'featured'
}) => {
    const displayTestimonials = testimonials.slice(0, maxItems);
    const featuredTestimonial = displayTestimonials[0];
    const otherTestimonials = displayTestimonials.slice(1);

    return (
        <section className="py-20 md:py-28" data-testid="testimonials-section">
            <div className="max-w-6xl mx-auto px-6">
                {/* Section header */}
                <div className="text-center mb-16">
                    <Heading as="h2" size="text-2xl md:text-3xl" className="text-[#1D6B6B] font-medium mb-4">
                        {title}
                    </Heading>
                    <BodyText className="text-[#7D8E95] text-lg">
                        {subtitle}
                    </BodyText>
                </div>

                {layout === 'featured' && showFeatured && featuredTestimonial ? (
                    <>
                        {/* Featured testimonial */}
                        <div className="mb-12">
                            <FeaturedTestimonial testimonial={featuredTestimonial} />
                        </div>
                        
                        {/* Other testimonials in grid */}
                        {otherTestimonials.length > 0 && (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {otherTestimonials.map((testimonial, index) => (
                                    <TestimonialCard 
                                        key={testimonial.id || index}
                                        testimonial={testimonial}
                                        variant="auto"
                                        index={index + 1}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    /* Standard grid layout */
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {displayTestimonials.map((testimonial, index) => (
                            <TestimonialCard 
                                key={testimonial.id || index}
                                testimonial={testimonial}
                                variant="auto"
                                index={index}
                            />
                        ))}
                    </div>
                )}

                {/* Trust indicators */}
                <div className="mt-12 text-center">
                    <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1D6B6B]/5 border border-[#1D6B6B]/10">
                        <div className="flex -space-x-2">
                            {['S', 'J', 'M', 'D'].map((initial, i) => (
                                <div 
                                    key={i}
                                    className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center text-white text-xs font-medium border-2 border-white"
                                >
                                    {initial}
                                </div>
                            ))}
                        </div>
                        <SmallText className="text-[#7D8E95] ml-2">
                            Join <span className="text-[#1D6B6B] font-semibold">1,000+</span> people finding their calm
                        </SmallText>
                    </div>
                </div>
            </div>
        </section>
    );
};

// Export components
export { TestimonialCard, FeaturedTestimonial, TestimonialsSection, DEFAULT_TESTIMONIALS };
export default TestimonialsSection;
